import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage, injectIntl } from 'react-intl';

import { NewWindow } from '@bibliocommons/base-icons';
import { Link } from '@bibliocommons/base-links';
import { libraryShape } from '@bibliocommons/bc-prop-types';
import Context from '@bibliocommons/context';
import Heading from '@bibliocommons/heading';
import naturalSortBy from '@bibliocommons/natural-sort-by';
import ProbeLogo from 'app/components/shared/probeLogo';
import { Table, TableHeader, TableHeading, TableBody, TableRow, TableCell } from '@bibliocommons/table';
import { bindAll } from '@bibliocommons/utils-react';

import ShortFormattedDate from 'app/components/shared/ShortFormattedDate';
import StackMapButton from 'app/components/bibs/shared/StackMap/StackMapButton';
import { AVAILABILITY_GROUP_ORDER, PROBES_AVAILABILITY_GROUP } from 'app/constants/BibConstants';
import bibItemShape from 'app/shapes/bibItemShape';
import stackMapStateShape from 'app/shapes/stackMapStateShape';
import AvailableGroupIcon from '../AvailableGroupIcon';
import UnAvailableGroupIcon from '../UnAvailableGroupIcon';

import './ItemAvailabilityTable.scss';

const GROUP_ICONS = {
  AVAILABLE_ITEMS: <AvailableGroupIcon />,
  AVAILABLE_BY_REQUEST_ITEMS: <AvailableGroupIcon />,
  LIBRARY_USE_ONLY_ITEMS: <AvailableGroupIcon />,
  RESTRICTED_ACCESS_ITEMS: <AvailableGroupIcon />,
  NOT_AVAILABLE_ITEMS: <UnAvailableGroupIcon />
};

const FIELDS = {
  ACTIONS: 'actions',
  AVAILABILITY: 'availability.libraryStatus',
  CALL_NUMBER: 'callNumber',
  COLLECTION: 'collection',
  COPY: 'copy',
  DUE_DATE: 'dueDate',
  LOCATION: 'branch.name',
  VOLUME: 'volume'
};

// Logic needed to programatically determine the width of
// of each column so that all columms across each table are aligned
const FIELD_WEIGHTS = {
  [FIELDS.LOCATION]: 4,
  [FIELDS.COLLECTION]: 4,
  [FIELDS.CALL_NUMBER]: 2,
  [FIELDS.COPY]: 1,
  [FIELDS.AVAILABILITY]: 2,
  [FIELDS.DUE_DATE]: 1.5,
  [FIELDS.ACTIONS]: 2,
  [FIELDS.VOLUME]: 1
};

function getColumnWidths(fields) {
  const totalWeight = fields.reduce((sum, field) => sum + (FIELD_WEIGHTS[field] || 1), 0);
  return fields.map(field => ((FIELD_WEIGHTS[field] || 1) / totalWeight) * 100);
}

function renderColGroup(fields) {
  const widths = getColumnWidths(fields);
  return (
    <colgroup>
      {fields.map((field, i) => (
        <col key={field} style={{ width: `${widths[i]}%` }} />
      ))}
    </colgroup>
  );
}

const getProbeStatusMessageId = (probeStatus, rollupStatus) => {
  const normalizedStatus = (probeStatus || rollupStatus || '').toLowerCase();
  return normalizedStatus ? `circ_${normalizedStatus}` : '';
};

const mapProbeToItem = (probe, intl) => {
  const availabilityLocationType = probe.getIn(['availability', 'availabilityLocationType']) || '';
  const probeStatus = probe.getIn(['availability', 'localisedStatus']) || '';
  const rollupStatus = probe.getIn(['availability', 'statusType']) || '';
  const statusMessageId = getProbeStatusMessageId(probeStatus, rollupStatus);
  const libraryStatus = statusMessageId
    ? intl.formatMessage({ id: statusMessageId }, { location: availabilityLocationType })
    : '';

  return Immutable.fromJS({
    availability: {
      group: PROBES_AVAILABILITY_GROUP,
      libraryStatus,
      status: probe.getIn(['availability', 'status'], ''),
      statusType: rollupStatus
    },
    bibUrl: probe.get('bibUrl', ''),
    branch: { name: probe.getIn(['library', 'longName'], '') },
    itemId: probe.getIn(['library', 'siteId'], '')
  });
};

function getAvailabilityGroup(item) {
  const availability = item.get('availability') || Immutable.Map();

  if (availability.get('group')) {
    return availability.get('group');
  }

  // Treat any item without a group as available.
  return item.get('inSiteScope') ? 'AVAILABLE_ITEMS' : 'EXTENDED_AVAILABLE_ITEMS';
}

function renderGroupHeading(rows) {
  const group = rows.first().get('group') || '';

  return (
    <Heading tagName="h3" size="medium" className="availability-group-heading">
      <span className="group-icon">{GROUP_ICONS[group.replace(/EXTENDED_|IN_SCOPE_/, '')]}</span>
      {group === PROBES_AVAILABILITY_GROUP ? (
        <FormattedMessage defaultMessage="Also found at" id="circ_also_available_count" values={{ count: rows.size }} />
      ) : (
        <FormattedMessage id={`circ_group_count_${group.toLowerCase()}`} values={{ count: rows.size }} />
      )}
    </Heading>
  );
}

function renderRows(rows, fields) {
  return rows.map(row => {
    const isProbe = row.get('group') === PROBES_AVAILABILITY_GROUP;

    return (
      <TableRow data-test-row-item-type={isProbe ? 'probe' : 'bib'} key={row.get('itemId')}>
        {fields.map((field, index) => {
          // This logic allows the Location Field cell for probes
          // to span 2 columns when the Availability Field cell
          // is not immediately after (to the right) of the Location Field cell.
          const isProbeNameCell = isProbe && field === FIELDS.LOCATION;
          const isLeftOfStatusCell = fields[index + 1] === FIELDS.AVAILABILITY;
          const isSkipCell = isProbe && !isProbeNameCell && isLeftOfStatusCell;
          if (isSkipCell) {
            return null;
          }

          const isDoubleColSpan = isProbeNameCell && !isLeftOfStatusCell;

          return (
            <TableCell colSpan={isDoubleColSpan ? 2 : 1} key={field} label={<FormattedMessage id={field} />} blockLabel>
              {isProbeNameCell ? (
                <>
                  <ProbeLogo circled className="probe-logo" small siteId={row.get('itemId')} />
                  <Link dataKey={row.get('itemId')} href={row.get('probeUrl')} rel="noopener noreferrer">
                    {row.getIn(['cells', FIELDS.LOCATION])}
                  </Link>
                </>
              ) : (
                row.getIn(['cells', field])
              )}
            </TableCell>
          );
        })}
      </TableRow>
    );
  });
}

export class ItemAvailabilityTable extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);

    this.state = {
      currentSortField: null,
      currentSortDirection: 'asc'
    };
  }

  isIncludeProbes() {
    const { currentLibrary, probes } = this.props;
    const { mobileApp: isMobileApp } = this.context;

    const isShowProbesOnBib = currentLibrary.get('showProbesOnBib');

    return isShowProbesOnBib && probes.size > 0 && !isMobileApp;
  }

  getSupportedFields() {
    const { currentLibrary } = this.props;

    const showCollectionInCirc = currentLibrary.get('showCollectionInCirc');
    const showDueDateInCirc = currentLibrary.get('showDueDateInCirc');
    const isIncludeActions = currentLibrary.get('hasRequestToView');
    const showVolumeInCirc = currentLibrary.get('showVolumeInCirc');
    const isIncludeLocation =
      this.isIncludeProbes() || currentLibrary.get('scopeFacetEnabled') || !currentLibrary.get('singleBranch');

    const supportedFields = [FIELDS.CALL_NUMBER, FIELDS.COPY, FIELDS.AVAILABILITY];

    if (showCollectionInCirc) {
      supportedFields.unshift(FIELDS.COLLECTION);
    }
    if (showDueDateInCirc) {
      supportedFields.push(FIELDS.DUE_DATE);
    }
    if (isIncludeLocation) {
      supportedFields.unshift(FIELDS.LOCATION);
    }
    if (isIncludeActions) {
      supportedFields.push(FIELDS.ACTIONS);
    }
    if (showVolumeInCirc) {
      supportedFields.push(FIELDS.VOLUME);
    }

    return supportedFields;
  }

  getTableItems(supportedFields) {
    const { bibItems, intl, probes } = this.props;
    const { currentSortField, currentSortDirection } = this.state;

    let sortedItems = bibItems;
    if (this.isIncludeProbes()) {
      sortedItems = sortedItems.concat(probes.map(probe => mapProbeToItem(probe, intl)));
    }

    if (currentSortField === FIELDS.LOCATION) {
      sortedItems = sortedItems.sortBy(item => item.getIn(['branch', 'name']));
      if (currentSortDirection === 'desc') {
        sortedItems = sortedItems.reverse();
      }
    } else if (currentSortField) {
      sortedItems = sortedItems.sort(naturalSortBy(currentSortField, currentSortDirection));
    }

    return sortedItems.map(item =>
      Immutable.Map({
        itemId: item.get('itemId'),
        group: getAvailabilityGroup(item),
        statusType: item.getIn(['availability', 'statusType']),
        cells: Immutable.Map(supportedFields.map(field => [field, this.renderCellContent(item, field)])),
        probeUrl: item.get('bibUrl', null)
      })
    );
  }

  handleSort(field) {
    if (this.state.currentSortField === field) {
      this.setState(state => ({
        currentSortDirection: state.currentSortDirection === 'asc' ? 'desc' : 'asc'
      }));
    } else {
      this.setState({
        currentSortField: field,
        currentSortDirection: 'asc'
      });
    }
  }

  renderCellContent(item, field) {
    const { intl, trackRequestFormClick, itemPosition, itemPositionGrouped, stackMapState, totalCount } = this.props;

    const isProbe = item.getIn(['availability', 'group']) === PROBES_AVAILABILITY_GROUP;

    switch (field) {
      case FIELDS.ACTIONS: {
        const requestFormUrl = item.get('requestFormUrl');
        if (!requestFormUrl) {
          return null;
        }

        const itemId = item.get('itemId');
        const linkText = intl.formatMessage({
          defaultMessage: 'Request this item',
          id: 'actions_request_item'
        });

        return (
          <Link
            dataKey={requestFormUrl}
            handleClick={() => {
              trackRequestFormClick({
                itemId,
                linkText
              });
            }}
            href={requestFormUrl}
            rel="noopener noreferrer"
          >
            <span className="request-link-label">{linkText}</span>
            <NewWindow />
          </Link>
        );
      }

      case FIELDS.AVAILABILITY: {
        const status = item.getIn(['availability', 'status']);
        const libraryStatus = item.getIn(['availability', 'libraryStatus']);
        const modifiedStatus =
          status === libraryStatus ? <FormattedMessage id={`circ_${status.toLowerCase()}`} /> : libraryStatus;
        return modifiedStatus && <span>{modifiedStatus}</span>;
      }

      case FIELDS.DUE_DATE: {
        const dueDate = item.get('dueDate');
        return dueDate && <ShortFormattedDate value={dueDate} />;
      }

      case FIELDS.COLLECTION: {
        return (
          !isProbe && (
            <>
              <div>{item.get('collection')}</div>
              <StackMapButton
                bibItem={item}
                fetchStackMap={this.props.handleStackMapOpen}
                itemPosition={itemPosition}
                itemPositionGrouped={itemPositionGrouped}
                stackMapState={stackMapState}
                totalCount={totalCount}
              />
            </>
          )
        );
      }

      default:
        return item.getIn(field.split('.'));
    }
  }

  renderCaption() {
    const { currentSortField, currentSortDirection } = this.state;
    return (
      currentSortField &&
      currentSortDirection && (
        <FormattedMessage
          id="table_sorted_by"
          values={{
            field: <FormattedMessage id={currentSortField} />,
            direction: <FormattedMessage id={currentSortDirection} />
          }}
        />
      )
    );
  }

  renderTableHeader(fields, { sortable = true } = {}) {
    const { currentSortField, currentSortDirection } = this.state;

    return (
      <TableHeader className={sortable ? undefined : 'sr-only'}>
        {fields.map(field =>
          sortable ? (
            <TableHeading
              sortable
              sortDirection={currentSortField === field ? currentSortDirection : undefined}
              handleSort={() => this.handleSort(field)}
              key={field}
            >
              <FormattedMessage id={field} />
            </TableHeading>
          ) : (
            <TableHeading key={field}>
              <FormattedMessage id={field} />
            </TableHeading>
          )
        )}
      </TableHeader>
    );
  }

  render() {
    const supportedFields = this.getSupportedFields();
    const tableItems = this.getTableItems(supportedFields);

    const groups = tableItems
      .groupBy(row => row.get('group'))
      .toList()
      .sortBy(rows => AVAILABILITY_GROUP_ORDER.indexOf(rows.first().get('group')));

    // Remove columns with no data.
    const fields = supportedFields.filter(field => tableItems.some(row => !!row.getIn(['cells', field])));
    const colGroup = renderColGroup(fields);

    return (
      <div className="cp-item-availability-table">
        {groups.map((rows, index) => {
          const isFirstTable = index === 0;
          const groupKey = rows.first().get('group');

          return (
            <div className="availability-group" key={groupKey}>
              {renderGroupHeading(rows)}
              <Table caption={isFirstTable ? this.renderCaption() : undefined} className="availability-table-fixed">
                {colGroup}
                {this.renderTableHeader(fields, { sortable: isFirstTable })}
                <TableBody>{renderRows(rows, fields)}</TableBody>
              </Table>
            </div>
          );
        })}
      </div>
    );
  }
}

ItemAvailabilityTable.propTypes = {
  bibItems: ImmutablePropTypes.listOf(bibItemShape).isRequired,
  currentLibrary: libraryShape.isRequired,
  handleStackMapOpen: PropTypes.func.isRequired,
  itemPosition: PropTypes.number,
  itemPositionGrouped: PropTypes.number,
  totalCount: PropTypes.number,
  probes: ImmutablePropTypes.list.isRequired,
  stackMapState: stackMapStateShape.isRequired,
  trackRequestFormClick: PropTypes.func.isRequired
};

ItemAvailabilityTable.contextType = Context;

export default injectIntl(ItemAvailabilityTable);
